/**
 * Admin: drag-and-drop batch emoji upload with per-file review.
 *
 * Flow:
 *   1. Admin drops N files (or picks via "Browse") → each lands in a
 *      staging slot with an auto-derived label + the batch's default
 *      categories.
 *   2. Per file, the admin can edit the label OR override the categories.
 *      Files can be removed individually from the batch.
 *   3. "Upload N file(s)" submits — we fire the existing /upload-direct
 *      endpoint once per file so per-file overrides are honoured without
 *      adding a new multipart variant on the backend.
 *   4. Progress bar updates as each file completes; errors surface
 *      inline next to the offending row but don't block the rest.
 *
 * Why per-file PUTs instead of one bulk endpoint? The /upload-direct
 * endpoint is stable and well-tested; parallel calls scale fine for the
 * 5-50-emoji batches a single admin would realistically prepare, and
 * keeping the backend surface narrow reduces the blast radius if anything
 * goes wrong.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Upload, X, Image as ImageIcon, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { getAuthHeaders } from '@/utils/authUtils';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_BYTES = 2 * 1024 * 1024;
const ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';

// Status enum for each staged file. Drives the per-row badge / colour.
const ST = {
  STAGED: 'staged',
  UPLOADING: 'uploading',
  DONE: 'done',
  FAILED: 'failed',
};

export const BatchUploadPanel = ({ defaultCategories, onComplete }) => {
  const [items, setItems] = useState([]);  // [{id, file, previewUrl, label, categories, status, error}]
  const [submitting, setSubmitting] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Revoke blob URLs on unmount so we don't leak. We intentionally
  // only clean up on unmount; per-item cleanup happens inside removeItem.
  useEffect(() => () => {
    items.forEach((i) => i.previewUrl && URL.revokeObjectURL(i.previewUrl));
  }, []);

  const deriveLabel = (filename) => {
    // Strip extension + clean up spacing/casing the same way the backend would
    // when filename is the only label cue.
    const base = (filename || '').replace(/\.[^.]+$/, '');
    return base.replace(/[_-]+/g, ' ').trim().replace(/\b\w/g, (c) => c.toUpperCase()).slice(0, 60);
  };

  const acceptFiles = useCallback((files) => {
    if (!files || files.length === 0) return;
    const rejected = [];
    const accepted = [];
    Array.from(files).forEach((f) => {
      if (f.size > MAX_BYTES) {
        rejected.push(`${f.name}: too large (${(f.size / 1024 / 1024).toFixed(1)} MB)`);
        return;
      }
      if (!f.type.startsWith('image/')) {
        rejected.push(`${f.name}: not an image`);
        return;
      }
      accepted.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file: f,
        previewUrl: URL.createObjectURL(f),
        label: deriveLabel(f.name),
        // Per-file categories default to the batch default — admin can
        // override on a per-row basis.
        categories: defaultCategories || '',
        status: ST.STAGED,
        error: null,
      });
    });
    if (rejected.length > 0) toast.error(`${rejected.length} file(s) skipped:\n${rejected.slice(0, 3).join('\n')}${rejected.length > 3 ? '\n…' : ''}`);
    if (accepted.length > 0) setItems((prev) => [...prev, ...accepted]);
  }, [defaultCategories]);

  // Drag-and-drop wiring. We use the simplest browser-native flow — no
  // react-dropzone dep — since the dropzone is a single rectangle and
  // we don't need directory-tree support.
  const handleDragEnter = (e) => { e.preventDefault(); setDragging(true); };
  const handleDragOver = (e) => { e.preventDefault(); };
  const handleDragLeave = (e) => {
    // Only fire when actually leaving the dropzone — drag-over child
    // elements briefly fires dragleave on the parent.
    if (e.currentTarget === e.target) setDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    acceptFiles(e.dataTransfer?.files || []);
  };

  const removeItem = (id) => {
    setItems((prev) => {
      const target = prev.find((i) => i.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((i) => i.id !== id);
    });
  };

  const updateItem = (id, patch) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  };

  const submit = useCallback(async () => {
    const queue = items.filter((i) => i.status === ST.STAGED);
    if (queue.length === 0) {
      toast.info('No new files to upload.');
      return;
    }
    setSubmitting(true);
    let ok = 0;
    let failed = 0;
    // Sequential rather than parallel: gives the admin a visible-progress
    // experience and prevents 10 simultaneous multipart writes from
    // saturating the backend disk on big batches.
    for (const item of queue) {
      updateItem(item.id, { status: ST.UPLOADING });
      try {
        const fd = new FormData();
        fd.append('files', item.file);
        if (item.categories.trim()) fd.append('categories', item.categories.trim());
        const res = await fetch(`${API}/api/admin/emojis/upload-direct`, {
          method: 'POST',
          body: fd,
          credentials: 'include',
          headers: getAuthHeaders(),
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.detail || `HTTP ${res.status}`);
        }
        const data = await res.json();
        // The endpoint returns the created emoji record(s). We update
        // the label on the new record if the admin renamed it from the
        // filename default.
        const created = data?.created?.[0];
        if (created && item.label && item.label !== created.label) {
          await fetch(`${API}/api/admin/emojis/${created.emoji_id}`, {
            method: 'PUT',
            credentials: 'include',
            headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ label: item.label }),
          }).catch(() => {/* non-fatal */});
        }
        updateItem(item.id, { status: ST.DONE });
        ok += 1;
      } catch (e) {
        updateItem(item.id, { status: ST.FAILED, error: e.message || 'Upload failed' });
        failed += 1;
      }
    }
    setSubmitting(false);
    if (ok > 0) toast.success(`Uploaded ${ok} emoji(s).`);
    if (failed > 0) toast.error(`${failed} file(s) failed — see inline errors.`);
    onComplete?.({ ok, failed });
  }, [items, onComplete]);

  // Clear successfully-uploaded items so the admin can start a fresh
  // batch without manually scrubbing the panel.
  const clearDone = () => {
    setItems((prev) => prev.filter((i) => i.status !== ST.DONE));
  };

  const stagedCount = items.filter((i) => i.status === ST.STAGED).length;
  const doneCount = items.filter((i) => i.status === ST.DONE).length;
  const failedCount = items.filter((i) => i.status === ST.FAILED).length;

  return (
    <Card data-testid="batch-upload-panel">
      <CardContent className="space-y-3 pt-4">
        {/* Dropzone */}
        <button
          type="button"
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`w-full border-2 border-dashed rounded-md p-6 text-center transition cursor-pointer ${
            dragging
              ? 'border-[#00E5CC] bg-[#00E5CC]/10 scale-[1.01]'
              : 'border-[#00A896]/30 bg-[#00E5CC]/5 hover:border-[#00A896]/50'
          }`}
          data-testid="batch-upload-dropzone"
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => { acceptFiles(e.target.files); e.target.value = ''; }}
            data-testid="batch-upload-input"
          />
          <Upload className="h-6 w-6 mx-auto text-[#00A896] mb-2" />
          <p className="text-sm font-medium">
            {dragging ? 'Drop to add to the batch' : 'Drag files here or click to browse'}
          </p>
          <p className="text-[11px] text-muted-foreground mt-1">
            PNG / JPG / WEBP / GIF · 2 MB per file · auto-resized for all 13 platforms
          </p>
        </button>

        {/* Staged items list */}
        {items.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs flex-wrap gap-2">
              <p className="font-medium">
                {items.length} file{items.length === 1 ? '' : 's'} ready
                {stagedCount !== items.length && (
                  <span className="text-muted-foreground"> · {doneCount} done · {failedCount} failed</span>
                )}
              </p>
              <div className="flex gap-1">
                {doneCount > 0 && (
                  <Button size="sm" variant="ghost" onClick={clearDone} className="h-6 px-2 text-[10px]" data-testid="batch-clear-done">
                    Clear done
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    items.forEach((i) => i.previewUrl && URL.revokeObjectURL(i.previewUrl));
                    setItems([]);
                  }}
                  className="h-6 px-2 text-[10px]"
                  disabled={submitting}
                  data-testid="batch-clear-all"
                >
                  Clear all
                </Button>
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto space-y-1.5 pr-1" data-testid="batch-staged-list">
              {items.map((item) => (
                <StagedRow
                  key={item.id}
                  item={item}
                  disabled={submitting || item.status !== ST.STAGED}
                  onChange={(patch) => updateItem(item.id, patch)}
                  onRemove={() => removeItem(item.id)}
                />
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button
                onClick={submit}
                disabled={submitting || stagedCount === 0}
                className="bg-[#00A896] text-white hover:bg-[#008b7a]"
                data-testid="batch-submit-btn"
              >
                {submitting
                  ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Uploading {stagedCount} of {items.length}…</>
                  : <><Upload className="h-4 w-4 mr-1" /> Upload {stagedCount} file{stagedCount === 1 ? '' : 's'}</>}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};


/** One row in the staged list — thumb + editable label + editable categories + status badge. */
const StagedRow = ({ item, disabled, onChange, onRemove }) => {
  const statusBadge = () => {
    switch (item.status) {
      case 'uploading':
        return <span className="flex items-center gap-1 text-[10px] text-blue-600 dark:text-blue-300"><Loader2 className="h-3 w-3 animate-spin" /> Uploading</span>;
      case 'done':
        return <span className="flex items-center gap-1 text-[10px] text-green-600 dark:text-green-400"><CheckCircle2 className="h-3 w-3" /> Done</span>;
      case 'failed':
        return <span className="flex items-center gap-1 text-[10px] text-red-600 dark:text-red-400" title={item.error}><AlertTriangle className="h-3 w-3" /> Failed</span>;
      default:
        return <span className="text-[10px] text-muted-foreground">Ready</span>;
    }
  };
  return (
    <div
      className={`flex gap-2 items-center p-2 rounded border text-xs ${
        item.status === 'done' ? 'border-green-500/20 bg-green-500/5'
        : item.status === 'failed' ? 'border-red-500/30 bg-red-500/5'
        : 'border-border bg-muted/20'
      }`}
      data-testid={`batch-row-${item.id}`}
    >
      <div className="w-10 h-10 shrink-0 bg-background/50 rounded border flex items-center justify-center overflow-hidden">
        {item.previewUrl ? (
          <img src={item.previewUrl} alt={item.label} className="w-full h-full object-contain" />
        ) : (
          <ImageIcon className="h-4 w-4 text-muted-foreground" />
        )}
      </div>
      <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-1 min-w-0">
        <Input
          value={item.label}
          onChange={(e) => onChange({ label: e.target.value.slice(0, 60) })}
          placeholder="Label"
          disabled={disabled}
          className="h-7 text-xs"
          data-testid={`batch-row-label-${item.id}`}
        />
        <Input
          value={item.categories}
          onChange={(e) => onChange({ categories: e.target.value })}
          placeholder="categories (comma-separated)"
          disabled={disabled}
          className="h-7 text-xs"
          data-testid={`batch-row-categories-${item.id}`}
        />
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        {statusBadge()}
        {item.status === 'staged' && (
          <button
            type="button"
            onClick={onRemove}
            className="text-muted-foreground hover:text-red-500 transition"
            title="Remove from batch"
            data-testid={`batch-row-remove-${item.id}`}
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
};

export default BatchUploadPanel;
